import { useCubeImages } from '@/composables/useCubeImages.ts';
import { useDate } from '@/composables/useDate.ts';
import { useMyCube } from '@/composables/useMyCube.js';
import { useUnits } from '@/composables/useUnits.ts';
import { useCubeStore } from '@/stores/useCubeStore';
import { useMapStore } from '@/stores/useMapStore';
import { usePortfolioStore } from '@/stores/usePortfolioStore';
import type { ProductionType } from '@/types/ProductionType';
import { BooleanUtil } from '@/util/BooleanUtil';
import { resolveSpecialPriceState } from '@/util/SpecialPriceResolver';
import { computed, isRef, type ComputedRef, type Ref } from 'vue';
import type { Router } from 'vue-router';

interface MapCubeParams {
  nickName: string
  energyLinesRequired: number
  openConnectionsAvailable: string
  infoWindowText?: string
  inverterPublicURL?: string
  allowDisplayProductionDetails?: boolean
  productionCapacityKWp?: number | null
  infoWindowImageDetails?: any
  latitude: string
  longitude: string
  publicContactPhoneNumber?: string;
}

interface MapCubeProductionDetails {
  avg2H: number
  prod1D: number
  prod1M: number
}

interface MapCube {
  cubeName: string
  hasProduction: boolean
  isProducer: boolean
  isConsumer: boolean
  isDonatee: boolean
  isContributor: boolean
  canReceiveDonations: boolean
  productionType: ProductionType
  productionDetails?: MapCubeProductionDetails | null
  params: MapCubeParams
  [key: string]: any
}

export function useSpecificCube(router: Router, cube: Ref<MapCube>) {
  if (!isRef(cube)) {
    throw new Error('useSpecificCube: Cube must be a ref, got ' + cube + ' instead.')
  }
  const cubeStore = useCubeStore()
  const mapStore = useMapStore()
  const portfolioStore = usePortfolioStore()
  const { isDemoUser } = useMyCube()
  const { formatDate } = useDate()
  const { formatKWH, formatKWP, formatWattage, formatPowerPrice } = useUnits()

  const isTour = computed(() => router.currentRoute.value.name === 'tour')

  const nickName = computed(() => cube.value.params.nickName)
  const cubeName = computed(() => cube.value.cubeName)
  const publicContactPhoneNumber = computed(() => cube.value.params.publicContactPhoneNumber)
  const isMe = computed(() => cubeStore.name === cube.value.cubeName)
  const hasProduction = computed(() => cube.value.hasProduction)
  const hasEnoughUnusedEnergyLinesToConnectTo = computed(() => cubeStore.unusedEnergyLines >= cube.value.params.energyLinesRequired)
  const energyLinesRequired = computed(() => cube.value.params.energyLinesRequired)
  const openConnectionsAvailable = computed(() => parseInt(cube.value.params.openConnectionsAvailable))
  const isProducerToMe = computed(() => cube.value.isProducer)
  const isConsumerOfMe = computed(() => cube.value.isConsumer)
  const isDonatee = computed(() => cube.value.isDonatee)
  const isContributor = computed(() => cube.value.isContributor)
  const canReceiveDonations = computed(() => cube.value.canReceiveDonations)
  const isInPortfolio = computed(() => portfolioStore.isCubeInPortfolio(cube.value.cubeName))

  const gridFeeDiscountApplies = computed(() => {
    if (!hasProduction.value) {
      return false
    }

    const prices = mapStore.getProducerPrices(cube.value.cubeName)
    return prices && prices.gridFeeDiscountApplies
  })

  const gridFeeDiscountPriceBrutto = computed(() => {
    if (!hasProduction.value) {
      return false
    }

    const prices = mapStore.getProducerPrices(cube.value.cubeName)
    return prices && prices.gridFeeDiscountApplies ? prices.gridFeeDiscountPriceBrutto : 0
  })

  const canAddConsumers = computed(() => {
    return hasProduction.value && openConnectionsAvailable.value > 0
  })

  const numLikes = computed(() => {
    // The number of likes is only updated in the global cubes list.
    // Since the list of producers/consumers/donatees is still kept separately
    // we need to make sure to use the value from global cubes list here.
    const globalCube = mapStore.getCubeByName(cube.value.cubeName, isTour.value ? 'tour' : 'global')
    return globalCube?.numLikes ?? 0
  })

  const canConnectToProducer = computed(() => {
    if (
      !canAddConsumers.value ||
      isMe.value ||
      isProducerToMe.value ||
      !hasEnoughUnusedEnergyLinesToConnectTo.value
    ) {
      return false
    }
    return true
  })

  const isLikedByMe = computed(() => cubeStore.likedCubes.some(n => n === cube.value.cubeName))
  const isLikeable = computed(() => cubeStore.likeableCubes.some(n => n === cube.value.cubeName))

  const toggleLike = () => {
    // TODO Add error handling instead of hoping the http request goes through.
    if (isLikedByMe.value) {
      cubeStore.unLikeCube(cube.value.cubeName)
    } else {
      cubeStore.likeCube(cube.value.cubeName)
    }
  }

  const onConnectToCubeClick = () => {
    if (isDemoUser.value) {
      // Demo users after the tour can't connect, but get redirected to the
      // choose provider page.
      router.push({
        name: 'choose-provider',
        params: {
          context: 'map',
        },
      })
    } else {
      // Regular users not in the tour, just connect.
      cubeStore.connectToCube(cube.value)
    }
  }

  const onDonateToCubeClick = () => {
    cubeStore.donateToCube(cube.value)
  }

  const onStopDonatingToCubeClick = () => {
    cubeStore.stopDonatingToCube(cube.value)
  }

  const addToPortfolio = () => {
    portfolioStore.addCubeToPortfolio(cube.value.cubeName)
  }

  const removeFromPortfolio = () => {
    portfolioStore.removeCubeFromPortfolio(cube.value.cubeName)
  }

  const isConnected = computed(() => {
    return isProducerToMe.value || isConsumerOfMe.value || isDonatee.value || isContributor.value
  })

  const resolvedProducerPriceState = computed(() => {
    const prices = mapStore.getProducerPrices(cube.value.cubeName, { freshOnly: true })
    if (!prices) {
      return resolveSpecialPriceState({})
    }

    if (prices.basePrices || prices.specialPrices) {
      return resolveSpecialPriceState(prices.basePrices ?? {}, prices.specialPrices)
    }

    const basePrices = prices.priceType === 'sonder'
      ? {}
      : {
          currentPrice: prices.currentPrice,
          nextPrice: prices.nextPrice,
          dateNextPrice: prices.dateNextPrice,
        }
    const specialPrices = prices.priceType === 'sonder'
      ? {
          currentPrice: prices.currentPrice,
          nextPrice: prices.nextPrice,
          dateNextPrice: prices.dateNextPrice,
          currentPriceValidUntil: prices.currentPriceValidUntil,
        }
      : undefined

    return resolveSpecialPriceState(basePrices, specialPrices)
  })

  const currentPrice = computed(() => {
    if (!hasProduction.value) {
      return false
    }
    return resolvedProducerPriceState.value.currentPrice ?? false
  })
  const currentPriceFormatted = computed(() => formatPowerPrice(currentPrice.value))
  const producerPricesLoadedFresh = computed(() => mapStore.producerPricesLoadedFresh)

  const nextPrice = computed(() => {
    if (!hasProduction.value || !resolvedProducerPriceState.value.hasNextPrice) {
      return false
    }
    return resolvedProducerPriceState.value.nextPrice ?? false
  })
  const hasNextPrice = computed(() => hasProduction.value && resolvedProducerPriceState.value.hasNextPrice)
  const nextPriceFormatted = computed(() => formatPowerPrice(nextPrice.value))

  const dateNextPrice = computed(() => {
    if (!hasProduction.value || !resolvedProducerPriceState.value.hasNextPrice) {
      return false
    }
    return resolvedProducerPriceState.value.nextDate || false
  })

  const nextPriceLabel = computed(() => {
    if (dateNextPrice.value) {
      return `Ab ${formatDate(dateNextPrice.value, 'date')}`
    }
    return ''
  })

  const descriptionText = computed(() => {
    if (cube.value.params.infoWindowText) {
      return cube.value.params.infoWindowText
    }
    return false
  })

  const {
    thumbnailUrl,
    iconUrlPng,
    iconUrlSvg,
    imageUrl,
    hasThumbnail,
  } = useCubeImages(cubeName);

  const inverterPublicUrl = computed(() => cube.value.params.inverterPublicURL ?? '')

  // TODO Move this to preprocessing or the backend?
  const allowDisplayProductionDetails = computed(() => {
    return BooleanUtil.isTrue(cube.value.params.allowDisplayProductionDetails)
  })

  const productionType: ComputedRef<ProductionType> = computed(() => cube.value.productionType)
  const productionCapacity = computed(() => {
    const capacity = cube.value.params.productionCapacityKWp ?? null
    if (capacity) {
      return formatKWP(capacity * 1000)
    }
    return 0
  })

  const productionAvgTwoHours = computed(() => {
    if (productionDetails.value) {
      return formatWattage(productionDetails.value.avg2H)
    }

    return 0
  })

  const productionDetails = computed(() => {
    const details = cube.value.productionDetails ?? null

    if (hasProduction.value && details) {
      return details
    }

    return undefined
  })

  const surplusYesterday = computed(() => {
    if (productionDetails.value) {
      return formatKWH(productionDetails.value.prod1D)
    }
    return 0
  })

  const surplusLastMonth = computed(() => {
    if (productionDetails.value) {
      return formatKWH(productionDetails.value.prod1M)
    }
    return 0
  })

  const coordinates = computed(() => {
    return {
      lat: parseFloat(cube.value.params.latitude),
      lng: parseFloat(cube.value.params.longitude),
    }
  })

  return {
    cubeName,
    nickName,
    iconUrlSvg,
    iconUrlPng,
    thumbnailUrl,
    hasThumbnail,
    imageUrl,
    hasProduction,
    hasEnoughUnusedEnergyLinesToConnectTo,
    openConnectionsAvailable,
    inverterPublicUrl,
    energyLinesRequired,
    productionCapacity,
    productionAvgTwoHours,
    surplusYesterday,
    surplusLastMonth,
    canReceiveDonations,
    numLikes,
    currentPrice,
    currentPriceFormatted,
    producerPricesLoadedFresh,
    nextPrice,
    hasNextPrice,
    nextPriceFormatted,
    dateNextPrice,
    nextPriceLabel,
    descriptionText,
    coordinates,
    productionType,
    gridFeeDiscountApplies,
    gridFeeDiscountPriceBrutto,

    isMe,
    isProducerToMe,
    isConsumerOfMe,
    isInPortfolio,
    isLikeable,
    isLikedByMe,
    isConnected,
    isContributor,
    isDonatee,
    canConnectToProducer,
    allowDisplayProductionDetails,

    onConnectToCubeClick,
    onDonateToCubeClick,
    onStopDonatingToCubeClick,
    canAddConsumers,
    toggleLike,
    addToPortfolio,
    removeFromPortfolio,
    publicContactPhoneNumber,

    // Allow access to the raw cube data is necessary.
    rawCube: computed(() => cube.value),
  }
}
